/* ─── printContract.ts ──────────────────────────────────────────────
 * Opens a new browser tab with a clean, print-ready HTML contract.
 * Calling window.print() inside that tab lets the user print or
 * save-as-PDF via the native browser dialog — zero backend needed.
 * ──────────────────────────────────────────────────────────────────*/

import Swal from 'sweetalert2';

export type PrintableContract = {
  contract_number: string;
  title: string;
  status: string;
  event_date?: string | null;
  event_location?: string | null;
  package_name?: string | null;
  total_amount: number;
  deposit_amount: number;
  balance_amount: number;
  client_name?: string | null;
  rendered_content?: string | null;
  signature_status?: string | null;
  client_signature?: string | null;
  signed_at?: string | null;
};

/* Prevents HTML injection inside the printed document */
function esc(str: string | null | undefined): string {
  if (!str) return "";
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function fmt(n: number): string {
  return "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2 });
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      weekday: "long", year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* ─── Main export ─────────────────────────────────────────────────── */
export function printContract(contract: PrintableContract, tenantName: string): void {
  const win = window.open("", "_blank", "width=920,height=760");
  if (!win) {
    // Fallback message if popup is blocked
    Swal.fire({
      icon: 'warning',
      title: 'Pop-ups blocked',
      text: 'Please allow pop-ups for this site to print / save PDF.',
      confirmButtonColor: '#C8A96E',
      confirmButtonText: 'OK',
    });
    return;
  }

  const isSigned    = contract.signature_status === "signed" && !!contract.client_signature;
  const hasContent  = !!contract.rendered_content?.trim();
  const isHtml      = hasContent && /<[a-z][\s\S]*>/i.test(contract.rendered_content ?? "");
  const balanceColor = Number(contract.balance_amount) > 0 ? "#b45309" : "#047857";
  const today        = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>${esc(contract.contract_number)} — ${esc(contract.title)}</title>
  <style>
    /* ── Base ── */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html { font-size: 14px; }
    body {
      font-family: 'Georgia', 'Times New Roman', serif;
      color: #1a1a1a;
      background: #fff;
      padding: 0;
    }

    /* ── Page wrapper ── */
    .page {
      max-width: 820px;
      margin: 0 auto;
      padding: 40px 48px 60px;
    }

    /* ── Screen-only toolbar ── */
    .toolbar {
      background: #f9f5ee;
      border-bottom: 2px solid #C8A96E;
      padding: 12px 48px;
      display: flex;
      align-items: center;
      gap: 12px;
      position: sticky;
      top: 0;
      z-index: 100;
    }
    .toolbar p {
      font-family: sans-serif;
      font-size: 11px;
      color: #888;
      flex: 1;
    }
    .btn-print {
      background: #C8A96E;
      color: #fff;
      border: none;
      padding: 10px 24px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 700;
      font-family: sans-serif;
      cursor: pointer;
      letter-spacing: 0.3px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: background 0.2s;
    }
    .btn-print:hover { background: #b8965a; }
    .btn-close {
      background: #e5e7eb;
      color: #374151;
      border: none;
      padding: 10px 18px;
      border-radius: 8px;
      font-size: 13px;
      font-weight: 600;
      font-family: sans-serif;
      cursor: pointer;
    }
    .btn-close:hover { background: #d1d5db; }

    /* ── Header ── */
    .doc-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      border-bottom: 2px solid #C8A96E;
      padding-bottom: 20px;
      margin-bottom: 28px;
    }
    .studio-block { }
    .studio-name {
      font-size: 20px;
      font-weight: 700;
      font-family: sans-serif;
      color: #111;
      letter-spacing: -0.3px;
    }
    .studio-sub {
      font-size: 11px;
      color: #888;
      font-family: sans-serif;
      margin-top: 2px;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .doc-title-block { text-align: right; }
    .doc-type {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #C8A96E;
      font-family: sans-serif;
      font-weight: 700;
    }
    .doc-title {
      font-size: 18px;
      font-weight: 700;
      color: #111;
      margin-top: 3px;
      font-family: sans-serif;
    }
    .doc-number {
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #888;
      margin-top: 3px;
    }
    .status-pill {
      display: inline-block;
      margin-top: 6px;
      padding: 2px 10px;
      border: 1px solid #C8A96E;
      border-radius: 20px;
      font-size: 10px;
      color: #C8A96E;
      font-family: sans-serif;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 1px;
    }

    /* ── Meta grid ── */
    .meta-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px 24px;
      margin-bottom: 28px;
      padding: 20px;
      background: #fafaf8;
      border: 1px solid #ede8de;
      border-radius: 8px;
    }
    .meta-item { }
    .meta-label {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #aaa;
      font-family: sans-serif;
      margin-bottom: 4px;
    }
    .meta-value {
      font-size: 13px;
      color: #222;
      font-family: sans-serif;
      font-weight: 500;
    }

    /* ── Section title ── */
    .section-title {
      font-size: 9px;
      text-transform: uppercase;
      letter-spacing: 2px;
      color: #C8A96E;
      font-family: sans-serif;
      font-weight: 700;
      margin-bottom: 12px;
      border-bottom: 1px solid #f0e8d5;
      padding-bottom: 6px;
    }

    /* ── Contract content ── */
    .contract-body {
      font-size: 12.5px;
      line-height: 1.8;
      color: #333;
      white-space: pre-wrap;
      margin-bottom: 32px;
    }

    /* ── Payment table ── */
    .payment-section { margin-bottom: 32px; }
    .payment-table {
      width: 100%;
      border-collapse: collapse;
      font-family: sans-serif;
    }
    .payment-table tr { border-bottom: 1px solid #f0f0f0; }
    .payment-table tr:last-child { border-bottom: 2px solid #1a1a1a; }
    .payment-table td {
      padding: 8px 4px;
      font-size: 13px;
      color: #333;
    }
    .payment-table td:last-child {
      text-align: right;
      font-weight: 600;
    }
    .pay-total {
      font-size: 15px;
      font-weight: 700;
      color: #111;
    }

    /* ── Signature ── */
    .sig-section { margin-bottom: 32px; }
    .sig-box {
      border: 1px solid #e0e0e0;
      border-radius: 8px;
      padding: 20px 24px;
    }
    .sig-name {
      font-size: 24px;
      font-family: 'Georgia', serif;
      color: #C8A96E;
      margin: 8px 0 12px;
      font-style: italic;
    }
    .sig-meta {
      font-family: sans-serif;
      font-size: 11px;
      color: #666;
      line-height: 1.6;
    }
    .sig-divider {
      border: none;
      border-top: 1px solid #ccc;
      margin: 14px 0;
    }
    .sig-unsigned-line {
      height: 40px;
      border-bottom: 1px solid #ccc;
      margin-bottom: 6px;
    }
    .sig-label {
      font-family: sans-serif;
      font-size: 10px;
      color: #aaa;
      text-transform: uppercase;
      letter-spacing: 1px;
    }
    .sig-date-line {
      margin-top: 20px;
      font-family: sans-serif;
      font-size: 12px;
      color: #555;
    }

    /* ── Footer ── */
    .doc-footer {
      margin-top: 40px;
      padding-top: 14px;
      border-top: 1px solid #eee;
      text-align: center;
      font-family: sans-serif;
      font-size: 10px;
      color: #bbb;
      line-height: 1.7;
    }

    /* ── Print media ── */
    @media print {
      .toolbar { display: none !important; }
      .page { padding: 0; max-width: 100%; }
      @page { margin: 15mm 12mm; size: A4; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .meta-grid { background: #fafaf8 !important; -webkit-print-color-adjust: exact; }
    }
  </style>
</head>
<body>

  <!-- ── Screen toolbar (hidden on print) ── -->
  <div class="toolbar">
    <p>Preview of contract <strong>${esc(contract.contract_number)}</strong>. To save as PDF, click Print and choose "Save as PDF" as the destination.</p>
    <button class="btn-close" onclick="window.close()">✕ Close</button>
    <button class="btn-print" onclick="window.print()">🖨 Print / Save PDF</button>
  </div>

  <!-- ── Document ── -->
  <div class="page">

    <!-- Header -->
    <div class="doc-header">
      <div class="studio-block">
        <div class="studio-name">${esc(tenantName)}</div>
        <div class="studio-sub">Photography Studio</div>
      </div>
      <div class="doc-title-block">
        <div class="doc-type">Photography Contract</div>
        <div class="doc-title">${esc(contract.title)}</div>
        <div class="doc-number">${esc(contract.contract_number)}</div>
        <div><span class="status-pill">${esc(contract.status)}</span></div>
      </div>
    </div>

    <!-- Meta: client + event -->
    <div class="meta-grid">
      <div class="meta-item">
        <div class="meta-label">Client Name</div>
        <div class="meta-value">${esc(contract.client_name) || "—"}</div>
      </div>
      <div class="meta-item">
        <div class="meta-label">Event Date</div>
        <div class="meta-value">${esc(contract.event_date) || "—"}</div>
      </div>
      <div class="meta-item">
        <div class="meta-label">Event Location</div>
        <div class="meta-value">${esc(contract.event_location) || "—"}</div>
      </div>
      <div class="meta-item">
        <div class="meta-label">Package / Service</div>
        <div class="meta-value">${esc(contract.package_name) || "—"}</div>
      </div>
    </div>

    <!-- Contract body -->
    ${hasContent ? `
    <div>
      <div class="section-title">Terms &amp; Conditions</div>
      ${isHtml
        ? `<div class="contract-body" style="white-space:normal;font-size:13px;line-height:1.7">${contract.rendered_content}</div>`
        : `<div class="contract-body">${esc(contract.rendered_content)}</div>`
      }
    </div>
    ` : ""}

    <!-- Payment summary -->
    <div class="payment-section">
      <div class="section-title">Payment Summary</div>
      <table class="payment-table">
        <tbody>
          <tr>
            <td>Total Contract Value</td>
            <td class="pay-total">${fmt(contract.total_amount)}</td>
          </tr>
          <tr>
            <td>Deposit / Retainer</td>
            <td>${fmt(contract.deposit_amount)}</td>
          </tr>
          <tr>
            <td>Remaining Balance Due</td>
            <td style="color:${balanceColor}">${fmt(contract.balance_amount)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Signature section -->
    <div class="sig-section">
      <div class="section-title">Signature</div>
      ${isSigned ? `
      <div class="sig-box">
        <div class="sig-meta">Electronically signed by</div>
        <div class="sig-name">${esc(contract.client_signature)}</div>
        <hr class="sig-divider"/>
        <div class="sig-meta">
          <strong>Date:</strong> ${fmtDate(contract.signed_at)}<br/>
          This electronic signature has the same legal effect as a handwritten signature.
        </div>
      </div>
      ` : `
      <div class="sig-box">
        <div class="sig-label">Client Signature</div>
        <div class="sig-unsigned-line"></div>
        <div class="sig-label">Print Name</div>
        <div class="sig-unsigned-line" style="margin-top:6px;"></div>
        <div class="sig-date-line">Date: ____________________________________</div>
      </div>
      `}
    </div>

    <!-- Footer -->
    <div class="doc-footer">
      <div>Generated by <strong>Pixory AI</strong> &mdash; Smart Business Control for Photographers</div>
      <div>Document prepared on ${esc(today)} &middot; ${esc(contract.contract_number)}</div>
    </div>

  </div><!-- /page -->

</body>
</html>`;

  win.document.write(html);
  win.document.close();
  win.focus();
}
